import React from "react";
import styled from "styled-components";
import { alpha, Box, Button, Stack, Tooltip } from "@mui/material";
import JoinLeftIcon from "@mui/icons-material/JoinLeft";
import excludeStyleProps from "src/utils/exclude-style-props";
import Icon from "src/components/icon";
import gitlabIcon from "src/assets/icons/gitlab-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import githubIcon from "src/assets/icons/github-int";
import slackIcon from "src/assets/icons/slack-int";
import googleWorkspaceIcon from "src/assets/icons/google-workspace-int";
import jiraIcon from "src/assets/icons/jira-int";
import notionIcon from "src/assets/icons/notion-int";
import { useAuth } from "src/components/providers/auth-context";
import TeamMemberCardActions from "src/scenes/teams/team/team-member-card-actions";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

const Card = styled.button.withConfig({
    shouldForwardProp: excludeStyleProps(["isMe"]),
})`
    width: ${({ expanded }) => (expanded ? "auto" : "276px")};
    min-width: ${({ expanded }) => (expanded ? "320px" : "320px")};
    height: ${({ expanded }) => (expanded ? "auto" : "87px")};
    background-color: ${({ theme }) => theme.palette.background.paper};
    text-transform: none;
    cursor: ${({ isInteractive }) => (isInteractive ? "pointer" : "initial")};
    color: ${({ theme }) => theme.palette.text.primary};
    justify-content: flex-start;
    border: 2px solid rgba(89, 195, 106, 0.25);
    border-radius: 4px;
    border: ${({ theme, isMe, expanded }) => {
        if (isMe && !expanded) return `2px solid ${alpha(theme.palette.primary.main, 0.25)}`;
        return "none";
    }};
    padding: 0;
    line-height: normal;
`;

const Name = styled.span`
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const JobTitle = styled.span`
    font-size: 12px;
    font-weight: 300;
    color: ${({ theme }) => theme.palette.primary.main};
    white-space: nowrap;
`;

const Email = styled.span`
    font-size: 10px;
    font-weight: 300;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const UnregisteredIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    min-width: 42px;
    height: 42px;
    color: ${({ theme }) => theme.palette.background.default};
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 50%;
`;

const OverlayIconWrapper = styled.div`
    position: absolute;
    bottom: -5px;
    right: -5px;
    padding: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background-color: ${({ theme }) => theme.palette.warning.light};
    border: 2px solid #3d3d3d;
    border-radius: 50%;
    color: ${({ theme }) => theme.palette.background.default};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const TeamUnregisteredCard = ({ member, onClick, expanded, isInteractive, onModalClose }) => {
    const { user } = useAuth();

    const isInvited = member && member.isInvited;

    const { displayName, email, isIntegrated } = member || {};
    return (
        <Card
            onClick={() => isInteractive && onClick(member)}
            isMe={user.id === member.id}
            expanded={expanded}
            isInteractive={isInteractive}
            as={expanded || !isInteractive ? "div" : Button}
        >
            <Box
                p={expanded ? 3 : 1}
                style={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    justifyContent: "baseline",
                    alignItems: "center",
                }}
            >
                <Stack spacing={3} style={{ width: "100%" }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        style={{ height: "100%" }}
                    >
                        <Tooltip
                            title={
                                <Stack>
                                    <p>
                                        Data from unregistered users is included in the team
                                        dashboard metrics.
                                    </p>
                                    {isInvited && (
                                        <p>
                                            This user will join the team plan upon accepting
                                            invitation.
                                        </p>
                                    )}
                                </Stack>
                            }
                        >
                            <UnregisteredIconWrapper>
                                <JoinLeftIcon />
                                {isInvited && (
                                    <OverlayIconWrapper>
                                        <HourglassTopIcon style={{ fontSize: "1rem" }} />
                                    </OverlayIconWrapper>
                                )}
                            </UnregisteredIconWrapper>
                        </Tooltip>
                        <Stack
                            direction="column"
                            spacing={0}
                            alignItems="flex-start"
                            style={{ overflow: "hidden" }}
                        >
                            {(displayName || email) && <Name>{displayName || email}</Name>}
                            <JobTitle>
                                Unregistered user {isInvited && "+ Invitation pending"}
                            </JobTitle>
                            {expanded && email && <Email>{email}</Email>}
                            <Stack direction="row" spacing={2} pt={0.5}>
                                <Icon
                                    svg={gitlabIcon}
                                    fill={
                                        isIntegrated && isIntegrated.gitlab === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={gitlabSelfManagedIcon}
                                    fill={
                                        isIntegrated && isIntegrated.gitlabSelfManaged === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={githubIcon}
                                    fill={
                                        isIntegrated && isIntegrated.github === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={slackIcon}
                                    fill={
                                        isIntegrated && isIntegrated.slack === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={googleWorkspaceIcon}
                                    fill={
                                        isIntegrated && isIntegrated.google === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={jiraIcon}
                                    fill={
                                        isIntegrated && isIntegrated.jira === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={notionIcon}
                                    fill={
                                        isIntegrated && isIntegrated.notion === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                    {expanded && (
                        <TeamMemberCardActions member={member} onModalClose={onModalClose} />
                    )}
                </Stack>
            </Box>
        </Card>
    );
};

export default TeamUnregisteredCard;
